import { useState, useEffect, useCallback } from "react";
import Input from "../../components/form/input/InputField";
import ListboxComponent from "../../components/common/ListboxComponent";
import { DatePicker } from "../../components/common/DatePicker";
import { styles } from "../../styles/pages/Quotation/QuotationAddStyles";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ModalOpenAeroIcon, DeleteProductRowIcon, CloseSuccessModalIcon } from "../../components/svg/Icons";
// Define interface for Listbox items
interface Item {
  id: number;
  name: string;
}

// Define interface for product/service rows
export interface Product {
  id: number;
  name: string;
  amount: number;
  description: string;
  qty?: number;
  showDescription?: boolean;
  discount?: number;
  discountType: number; // 1 for Percent, 2 for Amount
  product_total?: number;
  isService?: boolean;
  effectiveGst: number; // Computed GST for products (from SGST, CGST, IGST), or selected GST for services
  SGST?: number; // Only for products
  CGST?: number; // Only for products
  IGST?: number; // Only for products
}

// Define interface for predefined products in the modal
interface PredefinedProduct {
  id: number;
  name: string;
  amount: number;
  description: string;
  SGST?: number;
  CGST?: number;
  IGST?: number;
}

// Form errors interface
interface FormErrors {
  quotationNumber?: string;
  quotationSubject?: string;
  quotationType?: string;
  quotationStatus?: string;
  opportunity?: string;
  validUntil?: string;
  productErrors?: string[];
}

const gstOptions = [
  { value: 0, label: "0%" },
  { value: 5, label: "5%" },
  { value: 12, label: "12%" },
  { value: 18, label: "18%" },
  { value: 28, label: "28%" },
];

// Custom useModal hook with memoized functions
const useModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  const openModal = useCallback(() => setIsOpen(true), []);
  const closeModal = useCallback(() => setIsOpen(false), []);

  return { isOpen, openModal, closeModal };
};

// Define props interface for the Modal component
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
}

// Custom Modal component
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  className,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className={`relative ${className}`}>{children}</div>
    </div>
  );
};

const QuotationAdd = () => {
  const [quotationType, setQuotationType] = useState<number | null>(null);
  const [termsAndConditions, setTermsAndConditions] = useState("");
  const [opportunities, setOpportunities] = useState<Item[]>([]);
  const [products, setProducts] = useState<Product[]>([
    {
      id: 1,
      name: "",
      amount: 0,
      description: "",
      qty: 1,
      discount: 0,
      discountType: 1,
      effectiveGst: 0,
      SGST: 0,
      CGST: 0,
      IGST: 0,
    },
  ]);
  const {
    isOpen: isProductModalOpen,
    openModal: openProductModal,
    closeModal: closeProductModal,
  } = useModal();
  const {
    isOpen: isSuccessModalOpen,
    openModal: openSuccessModal,
    closeModal: closeSuccessModal,
  } = useModal();
  const [currentProductId, setCurrentProductId] = useState<number | null>(null);
  const [selectedProductIds, setSelectedProductId] = useState<number[]>([]);
  const [total, setTotal] = useState(0);
  const [groupTotal, setGroupTotal] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [totalGST, setTotalGST] = useState(0); // Consolidated GST total
  const [effectiveGst, setEffectiveGst] = useState(0); // Weighted average GST for display
  const [quotationNumber, setQuotationNumber] = useState("");
  const [quotationSubject, setQuotationSubject] = useState("");
  const [opportunity, setOpportunity] = useState<Item | null>(null);
  const [quotationStatus, setQuotationStatus] = useState<string | null>(null);
  const [validUntil, setValidUntil] = useState("");
  const [predefinedProducts, setPredefinedProducts] = useState<
    PredefinedProduct[]
  >([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [gstType, setGstType] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({ productErrors: [] });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Fetch quotation number from API
  useEffect(() => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      alert("No access token found");
      return;
    }
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/quotation-number`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const nextQuotationNumber = response.data.quotation_number;
        if (nextQuotationNumber) {
          setQuotationNumber(nextQuotationNumber);
        } else {
          alert(
            "Quotation number not provided by the server. Please log in again."
          );
        }
      })
      .catch((error) => {
        console.error(
          "Error fetching quotation number:",
          error.response ? error.response.data : error.message
        );
        alert(
          "Error fetching quotation number: " +
          (error.response?.data?.message || error.message)
        );
      });
  }, []);

  // Fetch opportunities
  useEffect(() => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      alert("No access token found");
      return;
    }

    axios
      .get(`${process.env.REACT_APP_API_URL}/api/opportunitie/name/list`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const formattedOpportunities = response.data.data.map((item: any) => ({
          id: item.id,
          name: item.opportunitie_name,
        }));
        setOpportunities(formattedOpportunities);
      })
      .catch((error) => {
        console.error(
          "Error fetching opportunities:",
          error.response ? error.response.data : error.message
        );
        alert(
          "Error fetching opportunities: " +
          (error.response?.data?.message || error.message)
        );
      });
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      alert("No access token found");
      return;
    }

    axios
      .get(`${process.env.REACT_APP_API_URL}/api/companywisegst`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const type = response.data?.data?.[0]?.gst_type;
        setGstType(type);
      })
      .catch((error) => {
        console.error(
          "Error fetching GST type:",
          error.response ? error.response.data : error.message
        );
      });
  }, []);

  // Fetch predefined products
  useEffect(() => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      alert("No access token found");
      return;
    }

    axios
      .post(
        `${process.env.REACT_APP_API_URL}/api/product-name-list`,
        { search: searchTerm },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((response) => {
        const formattedProducts = response.data.data.map((item: any) => ({
          id: item.id,
          name: item.prod_name,
          amount: parseFloat(item.amount) || 0,
          description: item.description || "",
          SGST: parseFloat(item.SGST) || 0,
          CGST: parseFloat(item.CGST) || 0,
          IGST: parseFloat(item.IGST) || 0,
        }));
        setPredefinedProducts(formattedProducts);
      })
      .catch((error) => {
        console.error(
          "Error fetching products:",
          error.response ? error.response.data : error.message
        );
        alert(
          "Error fetching products: " +
          (error.response?.data?.message || error.message)
        );
      });
  }, [searchTerm]);

  // Reset products when quotationType changes
  useEffect(() => {
    const isService = quotationType === 2;
    setProducts([
      {
        id: 1,
        name: "",
        amount: 0,
        description: "",
        ...(isService ? {} : { qty: 1 }),
        discount: 0,
        discountType: 1,
        effectiveGst: 0,
        SGST: 0,
        CGST: 0,
        IGST: 0,
      },
    ]);
    setSelectedProductId([]);
    setTermsAndConditions("");
    setErrors((prev) => ({ ...prev, productErrors: [] }));
    closeProductModal();
  }, [quotationType, closeProductModal]);

  // Calculate totals
  useEffect(() => {
    const isService = quotationType === 2;

    // Subtotal (before discount and taxes)
    const baseTotal = products.reduce((sum, product) => {
      return sum + product.amount * (isService ? 1 : product.qty || 1);
    }, 0);

    // Calculate total discount (apply discount before taxes)
    const totalDiscountInRupees = products.reduce((sum, product) => {
      const baseProductTotal =
        product.amount * (isService ? 1 : product.qty || 1);
      const productDiscount = product.discount || 0;
      const discountInRupees =
        product.discountType === 1
          ? baseProductTotal * (productDiscount / 100)
          : productDiscount;
      return sum + discountInRupees;
    }, 0);

    // Calculate total GST (after discount)
    const totalGST = products.reduce((sum, product) => {
      const baseProductTotal =
        product.amount * (isService ? 1 : product.qty || 1);
      const productDiscount = product.discount || 0;
      const discountInRupees =
        product.discountType === 1
          ? baseProductTotal * (productDiscount / 100)
          : productDiscount;
      const discountedProductTotal = baseProductTotal - discountInRupees;
      const taxRate = isService
        ? (product.effectiveGst || 0) / 100
        : product.IGST !== undefined &&
          product.IGST !== null &&
          product.IGST > 0
          ? (product.IGST || 0) / 100
          : ((product.SGST || 0) + (product.CGST || 0)) / 100;
      return sum + discountedProductTotal * taxRate;
    }, 0);

    // Weighted GST for display (based on discounted totals)
    const discountedBaseTotal = baseTotal - totalDiscountInRupees;
    const gstPercentage =
      discountedBaseTotal > 0 ? (totalGST / discountedBaseTotal) * 100 : 0;

    // Calculate group total (apply discount first, then taxes)
    const groupTotal = products.reduce((sum, product) => {
      const baseProductTotal =
        product.amount * (isService ? 1 : product.qty || 1);
      const productDiscount = product.discount || 0;
      const discountInRupees =
        product.discountType === 1
          ? baseProductTotal * (productDiscount / 100)
          : productDiscount;
      const discountedProductTotal = baseProductTotal - discountInRupees;
      const taxRate = isService
        ? (product.effectiveGst || 0) / 100
        : product.IGST !== undefined &&
          product.IGST !== null &&
          product.IGST > 0
          ? (product.IGST || 0) / 100
          : ((product.SGST || 0) + (product.CGST || 0)) / 100;
      const productTotalWithTax = discountedProductTotal * (1 + taxRate);
      return sum + productTotalWithTax;
    }, 0);

    setTotal(baseTotal);
    setTotalDiscount(totalDiscountInRupees);
    setTotalGST(totalGST);
    setEffectiveGst(gstPercentage);
    setGroupTotal(groupTotal);
  }, [products, quotationType]);

  // Form validation
  const validateForm = (): FormErrors => {
    const newErrors: FormErrors = { productErrors: [] };

    if (!quotationNumber.trim())
      newErrors.quotationNumber = "Quotation Number field  is required";
    if (!quotationType) newErrors.quotationType = "Quotation Type field is required";
    if (!quotationStatus || quotationStatus.trim() === "")
      newErrors.quotationStatus = "Quotation Status field is required";
    if (!opportunity) newErrors.opportunity = "Opportunity field is required";
    if (!validUntil) newErrors.validUntil = "Valid Till date field is required";

    const productErrors = products.map((product) => {
      const errorsForProduct: string[] = [];
      if (!product.name.trim())
        errorsForProduct.push("<strong>Product/Service name field is required</strong>");
      if (product.amount <= 0)
        errorsForProduct.push("<strong>Note: Price field must be greater than 0</strong>");
      if (quotationType === 1 && (!product.qty || product.qty <= 0))
        errorsForProduct.push("<strong>Quantity field must be greater than 0</strong>");
      if (
        product.discountType === 1 &&
        (product.discount || 0) > 0 &&
        ((product.discount || 0) < 1 || (product.discount || 0) > 100)
      )
        errorsForProduct.push("<strong>Percentage discount field must be between 1 and 100</strong>");
      if (
        product.discountType === 2 &&
        (product.discount || 0) > 0 &&
        (product.discount || 0) > product.amount * (product.qty || 1)
      )
        errorsForProduct.push("<strong>Discount amount field cannot exceed the price</strong>");
      return errorsForProduct.length > 0 ? errorsForProduct.join(". ") : "";
    });

    newErrors.productErrors = productErrors;

    if (!products.length || products.every((p) => !p.name.trim())) {
      newErrors.productErrors = ["<strong>At least one product/service field is required</strong>"];
    }

    return newErrors;
  };

  const addProductRow = () => {
    const isService = quotationType === 2;
    const maxId =
      products.length > 0 ? Math.max(...products.map((p) => p.id)) : 0;
    const newProduct: Product = {
      id: maxId + 1,
      name: "",
      amount: 0,
      description: "",
      ...(isService ? {} : { qty: 1 }),
      discount: 0,
      discountType: 1,
      effectiveGst: 0,
      SGST: 0,
      CGST: 0,
      IGST: 0,
    };
    setProducts([...products, newProduct]);
    setErrors((prev) => ({
      ...prev,
      productErrors: [...(prev.productErrors || []), ""],
    }));
  };

  const deleteProductRow = (id: number) => {
    const index = products.findIndex((product) => product.id === id);
    setProducts(products.filter((product) => product.id !== id));
    setErrors((prev) => {
      const newProductErrors = [...(prev.productErrors || [])];
      newProductErrors.splice(index, 1);
      return { ...prev, productErrors: newProductErrors };
    });
  };

  const openModal = (product: Product) => {
    if (quotationType !== 1) return;
    setCurrentProductId(product.id);
    const matchedProduct = predefinedProducts.find(
      (p) => p.name === product.name
    );
    setSelectedProductId(matchedProduct ? [matchedProduct.id] : []);
    openProductModal();
  };

  const handleCheckboxChange = (productId: number) => {
    setSelectedProductId((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAllChange = () => {
    if (selectedProductIds.length === predefinedProducts.length) {
      setSelectedProductId([]);
    } else {
      setSelectedProductId(predefinedProducts.map((product) => product.id));
    }
  };

  const saveProduct = () => {
    if (quotationType !== 1) return;
    if (selectedProductIds.length === 0) {
      alert("Please select at least one product.");
      return;
    }

    const selectedProducts = predefinedProducts.filter((p) =>
      selectedProductIds.includes(p.id)
    );

    let updatedProducts = currentProductId
      ? products.filter((product) => product.id !== currentProductId)
      : [...products];

    const maxId =
      updatedProducts.length > 0
        ? Math.max(...updatedProducts.map((p) => p.id))
        : 0;
    const newProducts = selectedProducts.map((selected, index) => {
      const effectiveGst =
        selected.IGST !== undefined && selected.IGST !== null
          ? selected.IGST
          : (selected.SGST || 0) + (selected.CGST || 0);

      return {
        id: maxId + index + 1,
        name: selected.name,
        amount: selected.amount,
        description: selected.description,
        qty: 1,
        effectiveGst: effectiveGst,
        SGST: selected.SGST,
        CGST: selected.CGST,
        IGST: selected.IGST,
        discount: 0,
        discountType: 1,
      };
    });

    updatedProducts = [...updatedProducts, ...newProducts].filter(
      (product) => product.name.trim() !== ""
    );

    setProducts(updatedProducts);

    updatedProducts.forEach((product, index) => {
      const errorsForProduct: string[] = [];
      if (!product.name.trim())
        errorsForProduct.push("<strong>Product/Service name field is required</strong>");
      if (product.amount <= 0)
        errorsForProduct.push("<strong>Note:Price field must be greater than 0</strong>");
      if (quotationType === 1 && (!product.qty || product.qty <= 0))
        errorsForProduct.push("<strong>Quantity field must be greater than 0</strong>");
      setErrors((prev) => {
        const newProductErrors = [...(prev.productErrors || [])];
        newProductErrors[index] =
          errorsForProduct.length > 0 ? errorsForProduct.join(". ") : "";
        return { ...prev, productErrors: newProductErrors };
      });
    });

    closeProductModal();
  };

  const toggleDiscountType = (index: number) => {
    const updatedProducts = [...products];
    updatedProducts[index].discountType =
      updatedProducts[index].discountType === 1 ? 2 : 1;
    setProducts(updatedProducts);

    const product = updatedProducts[index];
    const errorsForProduct: string[] = [];
    // if (!product.name.trim())
    //   errorsForProduct.push("<strong>Product/Service name field is required</strong>");
    // if (product.amount <= 0)
    //   errorsForProduct.push("<strong>Note:Price field must be greater than 0</strong>");
    if (quotationType === 1 && (!product.qty || product.qty <= 0))
      errorsForProduct.push("<strong>Quantity field must be greater than 0</strong>");
    if (
      product.discountType === 1 &&
      (product.discount || 0) > 0 &&
      ((product.discount || 0) < 1 || (product.discount || 0) > 100)
    ) {
      errorsForProduct.push("<strong>Percentage discount  field must be between 1 and 100</strong>");
    }
    if (
      product.discountType === 2 &&
      (product.discount || 0) > 0 &&
      (product.discount || 0) > product.amount * (product.qty || 1)
    ) {
      errorsForProduct.push("<strong>Discount amount field cannot exceed the product price</strong>");
    }

    setErrors((prev) => {
      const newProductErrors = [...(prev.productErrors || [])];
      newProductErrors[index] =
        errorsForProduct.length > 0 ? errorsForProduct.join(". ") : "";
      return { ...prev, productErrors: newProductErrors };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({ productErrors: [] });
    setIsSubmitting(true);

    const validationErrors = validateForm();

    const hasTopLevelErrors = Object.keys(validationErrors).some(
      (key) =>
        key !== "productErrors" && validationErrors[key as keyof FormErrors]
    );

    const hasProductErrors =
      validationErrors.productErrors?.some((error) => error.length > 0) ||
      validationErrors.productErrors?.[0] ===
      "At least one product/service field is required";

    if (hasTopLevelErrors || hasProductErrors) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    const token = sessionStorage.getItem("access_token");
    if (!token) {
      setErrors({ quotationNumber: "Authorization required. Please log in." });
      setIsSubmitting(false);
      navigate("/signin");
      return;
    }

    const isService = quotationType === 2;

    const formattedItems = products.map((product) => {
      const predefinedProduct = predefinedProducts.find(
        (p) => p.name === product.name
      );
      const itemTotal = (
        product.amount *
        (isService ? 1 : product.qty || 1) *
        (1 + (product.effectiveGst || 0) / 100) *
        (product.discountType === 1
          ? 1 - (product.discount || 0) / 100
          : 1 -
          Math.min(
            (product.discount || 0) /
            (product.amount * (isService ? 1 : product.qty || 1)),
            1
          ))
      ).toFixed(2);

      if (isService) {
        return {
          service_name: product.name,
          price: product.amount,
          service_GST: product.effectiveGst || 0,
          discount_type: product.discountType,
          discount: product.discount || 0,
          description: product.description,
          service_total: itemTotal,
        };
      } else {
        return {
          product_id: predefinedProduct ? predefinedProduct.id : null,
          product_name: product.name,
          price: product.amount,
          qty: product.qty || 1,
          SGST: product.SGST || 0,
          CGST: product.CGST || 0,
          IGST: product.IGST || 0,
          description: product.description,
          discount_type: product.discountType,
          discount: product.discount || 0,
          product_total: itemTotal,
        };
      }
    });

    const payload = {
      quotation_number: quotationNumber,
      quotation_type: quotationType,
      quotation_subject: quotationSubject,
      quotation_status: quotationStatus || "",
      opportunity_id: opportunity?.id.toString() || "",
      valid_until: validUntil,
      gst: effectiveGst.toFixed(2),
      total: total.toFixed(2),
      group_total: groupTotal.toFixed(2),
      [isService ? "services" : "products"]: formattedItems,
      terms_and_conditions: termsAndConditions,
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/quotation/add`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === 200) {
        openSuccessModal();
        setTimeout(() => {
          closeSuccessModal();
          setQuotationSubject("");
          setQuotationType(1);
          setQuotationStatus(null);
          setOpportunity(null);
          setValidUntil("");
          setProducts([
            {
              id: 1,
              name: "",
              amount: 0,
              description: "",
              qty: 1,
              discount: 0,
              discountType: 1,
              effectiveGst: 0,
              SGST: 0,
              CGST: 0,
              IGST: 0,
            },
          ]);
          setTermsAndConditions("");
          if (response.data.quotation_number) {
            setQuotationNumber(response.data.quotation_number);
          }
          navigate("/quotation-list");
        }, 2000);
      } else if (response.data.status === 422) {
        const apiErrors = response.data.message;
        if (apiErrors && typeof apiErrors === "object") {
          setErrors(
            Object.fromEntries(
              Object.entries(apiErrors).map(([key, value]) => [
                key,
                Array.isArray(value) ? value[0] : value,
              ])
            )
          );
        }
      } else if (response.data.status === 500) {
        setErrors({
          quotationNumber: response.data.message || "Internal Server Error",
        });
      } else {
        setErrors({
          quotationNumber: "Unexpected server response. Please try again.",
        });
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "An unexpected error occurred.";
      setErrors({ quotationNumber: errorMessage });
      if (error.response?.status === 422) {
        const apiErrors = error.response?.data?.errors;
        if (apiErrors && typeof apiErrors === "object") {
          setErrors(
            Object.fromEntries(
              Object.entries(apiErrors).map(([key, value]) => [
                key,
                Array.isArray(value) ? value[0] : value,
              ])
            )
          );
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateProductField = (
    index: number,
    field: keyof Product,
    value: any
  ) => {
    const updatedProducts = [...products];
    updatedProducts[index] = { ...updatedProducts[index], [field]: value };
    setProducts(updatedProducts);

    const errorsForProduct: string[] = [];
    const product = updatedProducts[index];

    if (!product.name.trim())
      errorsForProduct.push("<strong>Product/Service name field is required</strong>");
    if (product.amount <= 0)
      errorsForProduct.push("<strong>Note:Price field must be greater than 0</strong>");
    if (quotationType === 1 && (!product.qty || product.qty <= 0))
      errorsForProduct.push("<strong>Quantity field must be greater than 0</strong>");
    if (
      product.discountType === 1 &&
      (product.discount || 0) > 0 &&
      ((product.discount || 0) < 1 || (product.discount || 0) > 100)
    ) {
      errorsForProduct.push("<strong>Percentage discount must be between 1 and 100</strong>");
    }
    if (
      product.discountType === 2 &&
      (product.discount || 0) > 0 &&
      (product.discount || 0) > product.amount * (product.qty || 1)
    ) {
      errorsForProduct.push("<strong>Discount amount cannot exceed the price</strong>");
    }

    setErrors((prev) => {
      const newProductErrors = [...(prev.productErrors || [])];
      newProductErrors[index] =
        errorsForProduct.length > 0 ? errorsForProduct.join(". ") : "";
      return { ...prev, productErrors: newProductErrors };
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Add Quotation</h2>
      </div>

      <div className={styles.formContainer}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.opportunitieDetails}>
            <div>
              <label className={styles.label}>
                Quotation Number <span className={styles.required}>*</span>
              </label>
              <Input
                type="text"
                value={quotationNumber}
                onChange={(e) => setQuotationNumber(e.target.value)}
                className={styles.inputField}
                disabled
              />
              {errors.quotationNumber && (
                <p className="text-red-500 dark:text-red-400 text-xs mt-1">
                  {errors.quotationNumber}
                </p>
              )}
            </div>

            <div>
              <label className={styles.label}>Quotation Subject</label>
              <Input
                type="text"
                value={quotationSubject}
                onChange={(e) => {
                  setQuotationSubject(e.target.value);
                  setErrors((prev) => ({
                    ...prev,
                    quotationSubject: undefined,
                  }));
                }}
              />
            </div>

            <div>
              <ListboxComponent<Item>
                value={
                  quotationType
                    ? {
                      id: quotationType,
                      name: quotationType === 1 ? "Product" : "Service",
                    }
                    : { id: 0, name: "Select Quotation Type" }
                }
                onChange={(item) => {
                  if (item && item.id !== 0) {
                    setQuotationType(item.id);
                    setErrors((prev) => ({
                      ...prev,
                      quotationType: undefined,
                    }));
                  } else {
                    setQuotationType(null);
                  }
                }}
                items={[
                  // { id: 0, name: "Select Quotation Type" },
                  { id: 1, name: "Product" },
                  { id: 2, name: "Service" },
                ]}
                label="Quotation Type"
                displayKey="name"
                required
                error={errors.quotationType}
                dropdownClassName={styles.dropdownMenu}
              />
            </div>


            <div>
              <ListboxComponent<Item>
                value={
                  quotationStatus ? { id: 0, name: quotationStatus } : null
                }
                onChange={(item) => {
                  setQuotationStatus(item ? item.name : null);
                  setErrors((prev) => ({
                    ...prev,
                    quotationStatus: undefined,
                  }));
                }}
                items={[
                  { id: 1, name: "Close Won" },
                  { id: 2, name: "Close Lost" },
                  { id: 3, name: "In Process" },
                ]}
                label="Quotation Status"
                displayKey="name"
                required
                error={errors.quotationStatus}
                dropdownClassName={styles.dropdownMenu}
              />
            </div>

            <div>
              <ListboxComponent<Item>
                value={opportunity}
                onChange={(item) => {
                  setOpportunity(item);
                  setErrors((prev) => ({ ...prev, opportunity: undefined }));
                }}
                items={opportunities}
                label="Opportunity"
                displayKey="name"
                required
                error={errors.opportunity}
                dropdownClassName={styles.dropdownMenu}
              />
            </div>

            <div>
              <DatePicker
                value={validUntil}
                onChange={(dates) => {
                  const date = dates[0];
                  if (date) {
                    const dd = String(date.getDate()).padStart(2, "0");
                    const mm = String(date.getMonth() + 1).padStart(2, "0");
                    const yyyy = date.getFullYear();
                    setValidUntil(`${dd}-${mm}-${yyyy}`);
                    setErrors((prev) => ({ ...prev, validUntil: undefined }));
                  } else {
                    setValidUntil("");
                    setErrors((prev) => ({ ...prev, validUntil: undefined }));
                  }
                }}
                label="Valid Till"
                required
                error={errors.validUntil}
              />
            </div>

            <div className="col-span-1 sm:col-span-3 mt-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3">
                <label className={`${styles.labelmain}`}>
                  {quotationType === 1 ? "Products" : "Services"}
                </label>
                <button
                  type="button"
                  onClick={addProductRow}
                  className="mt-2 sm:mt-0 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm bg-[#2f6aa7] dark:bg-[#3d6794] text-white rounded-lg hover:bg-[#2779bd] dark:hover:bg-[#335877] transition-colors duration-200"
                >
                  + Add {quotationType === 1 ? "Product" : "Service"}
                </button>
              </div>
              <div className="overflow-x-auto shadow-sm rounded-lg">
                <div
                  className={`grid ${quotationType === 1
                    ? gstType === "IGST"
                      ? "grid-cols-9"
                      : "grid-cols-10"
                    : "grid-cols-9"
                    } gap-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 py-2 px-3 sticky top-0 text-xs sm:text-sm min-w-[800px]`}
                >
                  <div className={`${styles.label} col-span-2 font-medium`}>
                    {quotationType === 1 ? "Product" : "Service"} Name
                  </div>
                  <div className={`${styles.label} font-medium`}>Price</div>
                  {quotationType === 1 && (
                    <div className={`${styles.label} font-medium`}>Qty</div>
                  )}
                  <div className={`${styles.label} font-medium`}>Discount</div>
                  <div className={`${styles.label} font-medium`}>Type</div>
                  {quotationType === 1 ? (
                    gstType === "IGST" ? (
                      <div className={`${styles.label} font-medium`}>IGST</div>
                    ) : (
                      <>
                        <div className={`${styles.label} font-medium`}>
                          SGST
                        </div>
                        <div className={`${styles.label} font-medium`}>
                          CGST
                        </div>
                      </>
                    )
                  ) : (
                    <div className={`${styles.label} font-medium`}>GST</div>
                  )}
                  <div className={`${styles.label} font-medium`}>Total</div>
                </div>
                {products.map((product, index) => (
                  <div
                    key={product.id}
                    className={`grid ${quotationType === 1
                      ? gstType === "IGST"
                        ? "grid-cols-9"
                        : "grid-cols-10"
                      : "grid-cols-9"
                      } gap-2 items-center py-2 px-3 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 min-w-[800px]`}
                  >
                    <div className="col-span-2 flex items-center gap-2">
                      {quotationType === 1 ? (
                        <>
                          <span className="text-xs sm:text-sm text-gray-800 dark:text-white/90 truncate">
                            {product.name || "Select Product"}
                          </span>
                          <button
                            type="button"
                            onClick={() => openModal(product)}
                            className="p-1 bg-brand-500 rounded-md hover:bg-brand-600 transition-colors duration-200"
                            disabled={quotationType !== 1}
                          >
                            <ModalOpenAeroIcon />
                          </button>
                        </>
                      ) : (
                        <Input
                          type="text"
                          value={product.name}
                          onChange={(e) =>
                            updateProductField(index, "name", e.target.value)
                          }
                          className={`${styles.inputField} text-xs sm:text-sm`}
                          placeholder="Service Name"
                        />
                      )}
                    </div>
                    <div>
                      <Input
                        type="text"
                        value={product.amount || ""}
                        onChange={(e) =>
                          updateProductField(
                            index,
                            "amount",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        className={`${styles.inputField} text-xs sm:text-sm`}
                        placeholder="Price"
                        disabled={quotationType === 1}
                      />
                    </div>
                    {quotationType === 1 && (
                      <div>
                        <Input
                          type="number"
                          className={`${styles.inputField} text-xs sm:text-sm`}
                          placeholder="Qty"
                          value={product.qty}
                          onChange={(e) =>
                            updateProductField(
                              index,
                              "qty",
                              parseInt(e.target.value) || 1
                            )
                          }
                        />
                      </div>
                    )}
                    <div>
                      <Input
                        type="text"
                        className={`${styles.inputField} text-xs sm:text-sm`}
                        placeholder="Discount"
                        value={product.discount}
                        onChange={(e) =>
                          updateProductField(
                            index,
                            "discount",
                            parseFloat(e.target.value) || 0
                          )
                        }
                      />
                    </div>
                    <div className="flex items-center justify-center">
                      <div className="flex w-20 sm:w-40 h-8 sm:h-10 bg-gray-300 rounded-full p-1">
                        <button
                          type="button"
                          onClick={() => toggleDiscountType(index)}
                          className={`flex-1 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 ${product.discountType === 1
                            ? "bg-brand-500 text-white"
                            : "text-black"
                            }`}
                        >
                          %
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleDiscountType(index)}
                          className={`flex-1 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 ${product.discountType === 2
                            ? "bg-brand-500 text-white"
                            : "text-black"
                            }`}
                        >
                          ₹
                        </button>
                      </div>
                    </div>
                    {quotationType === 1 ? (
                      gstType === "IGST" ? (
                        <div className="relative">
                          <Input
                            type="text"
                            value={
                              product.IGST !== undefined ? product.IGST : 0
                            }
                            className={`${styles.inputField} text-xs sm:text-sm`}
                            placeholder="IGST"
                            disabled
                          />
                          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs sm:text-sm pointer-events-none">
                            %
                          </span>
                        </div>
                      ) : (
                        <>
                          <div className="relative">
                            <Input
                              type="text"
                              value={
                                product.SGST !== undefined ? product.SGST : 0
                              }
                              className={`${styles.inputField} text-xs sm:text-sm`}
                              placeholder="SGST"
                              disabled
                            />
                            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs sm:text-sm pointer-events-none">
                              %
                            </span>
                          </div>
                          <div className="relative">
                            <Input
                              type="text"
                              value={
                                product.CGST !== undefined ? product.CGST : 0
                              }
                              className={`${styles.inputField} text-xs sm:text-sm`}
                              placeholder="CGST"
                              disabled
                            />
                            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs sm:text-sm pointer-events-none">
                              %
                            </span>
                          </div>
                        </>
                      )
                    ) : (
                      <div>
                        <select
                          value={product.effectiveGst || 0}
                          onChange={(e) =>
                            updateProductField(
                              index,
                              "effectiveGst",
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className={`${styles.inputField} text-xs sm:text-sm w-full appearance-none bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 focus:ring-brand-500`}
                        >
                          {gstOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <Input
                        type="text"
                        className={`${styles.inputField} text-xs sm:text-sm`}
                        placeholder="Total"
                        value={(() => {
                          const baseProductTotal =
                            product.amount *
                            (quotationType === 1 ? product.qty || 1 : 1);
                          const productDiscount = product.discount || 0;
                          const discountInRupees =
                            product.discountType === 1
                              ? baseProductTotal * (productDiscount / 100)
                              : productDiscount;
                          const discountedProductTotal =
                            baseProductTotal - discountInRupees;
                          const taxRate =
                            quotationType === 2 ||
                              (product.IGST !== undefined &&
                                product.IGST !== null &&
                                product.IGST > 0)
                              ? (product.IGST || product.effectiveGst || 0) /
                              100
                              : ((product.SGST || 0) + (product.CGST || 0)) /
                              100;
                          return (
                            discountedProductTotal *
                            (1 + taxRate)
                          ).toFixed(2);
                        })()}
                        disabled
                      />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => deleteProductRow(product.id)}
                        className="p-1 bg-red-500 rounded-md hover:bg-red-600 transition-colors duration-200"
                      >
                        <DeleteProductRowIcon />
                      </button>
                    </div>
                    <div className="col-span-11 mt-2">
                      <textarea
                        rows={4}
                        value={product.description}
                        disabled={quotationType === 1}
                        onChange={(e) =>
                          updateProductField(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        className={`${styles.inputField} text-xs sm:text-sm flex-1 min-h-[4rem]`}
                        placeholder="Description"
                        style={{ textAlign: "justify" }}
                      />
                      {errors.productErrors?.[index] && (
                        <p
                          className="text-red-500 dark:text-red-400 text-xs mt-1"
                          dangerouslySetInnerHTML={{ __html: errors.productErrors[index] }}
                        ></p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <label className={`${styles.label}`}>
                    Terms and Conditions
                  </label>
                  <textarea
                    rows={4}
                    value={termsAndConditions}
                    onChange={(e) => {
                      setTermsAndConditions(e.target.value);
                      setErrors((prev) => ({}));
                    }}
                    className={`${styles.textareaField} w-full text-xs sm:text-sm`}
                    style={{ textAlign: "justify" }}
                  />
                </div>
                <div className="flex flex-col gap-2 sm:w-[18rem]">
                  <div className="flex items-center gap-2 sm:gap-4">
                    <label className={`${styles.label} w-24 sm:w-32`}>
                      Subtotal:
                    </label>
                    <Input
                      type="text"
                      value={`₹${total.toFixed(2)}`}
                      className={`${styles.inputField} flex-1 text-xs sm:text-sm`}
                      disabled
                    />
                  </div>
                  <div className="flex items-center gap-2 sm:gap-4">
                    <label className={`${styles.label} w-24 sm:w-32`}>
                      Discount:
                    </label>
                    <Input
                      type="text"
                      value={`₹${totalDiscount.toFixed(2)}`}
                      className={`${styles.inputField} flex-1 text-xs sm:text-sm`}
                      disabled
                    />
                  </div>
                  {quotationType === 1 ? (
                    gstType === "IGST" ? (
                      <div className="flex items-center gap-2 sm:gap-4">
                        <label className={`${styles.label} w-24 sm:w-32`}>
                          IGST Total:
                        </label>
                        <Input
                          type="text"
                          value={`₹${products
                            .reduce((sum, product) => {
                              const baseProductTotal =
                                product.amount * (product.qty || 1);
                              const productDiscount = product.discount || 0;
                              const discountInRupees =
                                product.discountType === 1
                                  ? baseProductTotal * (productDiscount / 100)
                                  : productDiscount;
                              const discountedProductTotal =
                                baseProductTotal - discountInRupees;
                              return (
                                sum +
                                (discountedProductTotal * (product.IGST || 0)) /
                                100
                              );
                            }, 0)
                            .toFixed(2)}`}
                          className={`${styles.inputField} flex-1 text-xs sm:text-sm`}
                          disabled
                        />
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 sm:gap-4">
                          <label className={`${styles.label} w-24 sm:w-32`}>
                            SGST Total:
                          </label>
                          <Input
                            type="text"
                            value={`₹${products
                              .reduce((sum, product) => {
                                const baseProductTotal =
                                  product.amount * (product.qty || 1);
                                const productDiscount = product.discount || 0;
                                const discountInRupees =
                                  product.discountType === 1
                                    ? baseProductTotal * (productDiscount / 100)
                                    : productDiscount;
                                const discountedProductTotal =
                                  baseProductTotal - discountInRupees;
                                return (
                                  sum +
                                  (discountedProductTotal *
                                    (product.SGST || 0)) /
                                  100
                                );
                              }, 0)
                              .toFixed(2)}`}
                            className={`${styles.inputField} flex-1 text-xs sm:text-sm`}
                            disabled
                          />
                        </div>
                        <div className="flex items-center gap-2 sm:gap-4">
                          <label className={`${styles.label} w-24 sm:w-32`}>
                            CGST Total:
                          </label>
                          <Input
                            type="text"
                            value={`₹${products
                              .reduce((sum, product) => {
                                const baseProductTotal =
                                  product.amount * (product.qty || 1);
                                const productDiscount = product.discount || 0;
                                const discountInRupees =
                                  product.discountType === 1
                                    ? baseProductTotal * (productDiscount / 100)
                                    : productDiscount;
                                const discountedProductTotal =
                                  baseProductTotal - discountInRupees;
                                return (
                                  sum +
                                  (discountedProductTotal *
                                    (product.CGST || 0)) /
                                  100
                                );
                              }, 0)
                              .toFixed(2)}`}
                            className={`${styles.inputField} flex-1 text-xs sm:text-sm`}
                            disabled
                          />
                        </div>
                      </>
                    )
                  ) : (
                    <div className="flex items-center gap-2 sm:gap-4">
                      <label className={`${styles.label} w-24 sm:w-32`}>
                        GST Total:
                      </label>
                      <Input
                        type="text"
                        value={`₹${totalGST.toFixed(2)}`}
                        className={`${styles.inputField} flex-1 text-xs sm:text-sm`}
                        disabled
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-2 sm:gap-4">
                    <label className={`${styles.label} w-24 sm:w-32`}>
                      Group Total:
                    </label>
                    <Input
                      type="text"
                      value={`₹${groupTotal.toFixed(2)}`}
                      className={`${styles.inputField} flex-1 text-xs sm:text-sm`}
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.buttonsContainer}>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>

            <Link to="/quotation-list">
              <button type="button" className={styles.cancelButton}>
                Cancel
              </button>
            </Link>
          </div>
        </form>
      </div>

      {/* Product Selection Modal */}
      <Modal
        isOpen={isProductModalOpen && quotationType === 1}
        onClose={closeProductModal}
        className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 w-[90%] max-w-[400px] sm:max-w-5xl shadow-xl"
      >
        <h3 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-white mb-4">
          Select Products
        </h3>
        <div className="mb-4">
          <Input
            type="text"
            placeholder="Search products..."
            className={`${styles.inputField} w-full text-xs sm:text-sm`}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="max-h-60 sm:max-h-80 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
          <table className="w-full text-xs sm:text-sm text-gray-800 dark:text-white/90 min-w-[600px]">
            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
              <tr>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left">
                  <input
                    type="checkbox"
                    id="select-all"
                    checked={
                      selectedProductIds.length === predefinedProducts.length
                    }
                    onChange={handleSelectAllChange}
                    className="mr-1 sm:mr-2 rounded"
                  />
                  <label
                    htmlFor="select-all"
                    className="text-xs sm:text-sm font-medium"
                  >
                    Select All
                  </label>
                </th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left">Name</th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left">Amount</th>
                {gstType === "IGST" ? (
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left">IGST</th>
                ) : (
                  <>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left">
                      SGST
                    </th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left">
                      CGST
                    </th>
                  </>
                )}
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left">
                  Description
                </th>
              </tr>
            </thead>
            <tbody>
              {predefinedProducts.map((product) => (
                <tr
                  key={product.id}
                  className={`border-b border-gray-200 dark:border-gray-700 transition-colors duration-150 ${selectedProductIds.includes(product.id)
                    ? "bg-brand-50 dark:bg-brand-900/20"
                    : "hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                >
                  <td className="px-2 sm:px-4 py-2 sm:py-3">
                    <input
                      type="checkbox"
                      id={`product-${product.id}`}
                      checked={selectedProductIds.includes(product.id)}
                      onChange={() => handleCheckboxChange(product.id)}
                      className="rounded"
                    />
                  </td>
                  <td className="px-2 sm:px-4 py-2 sm:py-3">{product.name}</td>
                  <td className="px-2 sm:px-4 py-2 sm:py-3">
                    ₹{product.amount}
                  </td>
                  {gstType === "IGST" ? (
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      {typeof product.IGST === "number" && product.IGST > 0
                        ? `${product.IGST}%`
                        : "-"}
                    </td>
                  ) : (
                    <>
                      <td className="px-2 sm:px-4 py-2 sm:py-3">
                        {typeof product.SGST === "number" && product.SGST > 0
                          ? `${product.SGST}%`
                          : "-"}
                      </td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3">
                        {typeof product.CGST === "number" && product.CGST > 0
                          ? `${product.CGST}%`
                          : "-"}
                      </td>
                    </>
                  )}
                  <td className="px-2 sm:px-4 py-2 sm:py-3 truncate max-w-[150px] sm:max-w-xs">
                    {product.description}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-4 sm:mt-6 sticky bottom-0 bg-white dark:bg-gray-800 py-3 sm:py-4">
          <button
            type="button"
            onClick={saveProduct}
            className="px-4 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors duration-200"
          >
            Save Products
          </button>
          <button
            type="button"
            onClick={closeProductModal}
            className="px-4 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-sm bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors duration-200"
          >
            Cancel
          </button>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal
        isOpen={isSuccessModalOpen}
        onClose={closeSuccessModal}
        className="w-[80%] max-w-[600px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
      >
        <button
          onClick={closeSuccessModal}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <CloseSuccessModalIcon />
        </button>
        <div className="flex justify-center">
          <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>
        <h4 className="text-lg font-bold text-gray-800 dark:text-white mt-4">
          Quotation Added Successfully
        </h4>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          The Quotation has been added to the system successfully.
        </p>
        <div className="mt-6 flex justify-center">
          <button
            onClick={closeSuccessModal}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            OK
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default QuotationAdd;
